import { Address, formatDate, ProviderGuarantor, ProviderPaymentPlan, SupportCreditCard } from '@enter/core';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { RootStore } from 'stores';
import * as S from './billing-timeline-table.styles';

interface IGuarantorDetailProps {
    claimId: string;
    creditCards: SupportCreditCard[] | undefined;
    guarantor: ProviderGuarantor;
    paymentPlans: ProviderPaymentPlan[] | undefined;
}

export const GuarantorDetail: React.FunctionComponent<IGuarantorDetailProps> = inject('rootStore')(
    observer(
        (props: IGuarantorDetailProps): React.ReactElement => {
            const rootStore: RootStore = _.get(props, 'rootStore');
            if (!rootStore) {
                return <></>;
            }
            const { creditCards, guarantor, paymentPlans } = props;

            const renderAddress = (address: Address, i: number): React.ReactElement => (
                <S.TR key={`address-${i}`}>
                    <S.TH>Address</S.TH>
                    <S.TD>
                        {address.lines.join('\n')}
                        <br />
                        {address.city}, {address.state} {address.postal}
                    </S.TD>
                </S.TR>
            );

            const renderCreditCard = (cc: SupportCreditCard): React.ReactElement => (
                <S.TR key={cc.id}>
                    <S.TH>Credit Card</S.TH>
                    <S.TD>
                        {cc.brand} ...{cc.last4Digits} {cc.expirationMonth}/{cc.expirationYear}
                    </S.TD>
                </S.TR>
            );

            const renderPaymentPlan = (pp: ProviderPaymentPlan): React.ReactElement => {
                const { id, amountPerCycle, intervalCount, intervalSpan } = pp;
                const interval = intervalCount && intervalCount > 1 ? intervalCount : '';
                const plural = intervalCount && intervalCount > 1 ? 's' : '';
                return (
                    <S.TR key={id}>
                        <S.TH>Payment Plan</S.TH>
                        <S.TD>
                            ${_.get(amountPerCycle, 'amount')} payment every {interval} {_.toLower(intervalSpan)}
                            {plural}
                        </S.TD>
                    </S.TR>
                );
            };

            const renderDetails = (): React.ReactElement => {
                const {
                    id,
                    first,
                    middle,
                    last,
                    suffix,
                    organization,
                    dob,
                    phone,
                    email,
                    addresses,
                    dashboardUrl,
                } = guarantor;
                const addressRows = _.map(addresses, (address, i) => renderAddress(address, i));
                const creditCardRows = _.map(creditCards, (cc, i) => renderCreditCard(cc));
                const paymentPlanRows = _.map(paymentPlans, paymentPlan => renderPaymentPlan(paymentPlan));
                return (
                    <S.Table expand={true}>
                        <S.TBody key={id}>
                            <S.TR>
                                <S.TH>Name</S.TH>
                                <S.TD>
                                    {first} {middle} {last} {suffix}
                                </S.TD>
                            </S.TR>
                            <S.TR>
                                <S.TH>Organization</S.TH>
                                <S.TD>{organization || '--'}</S.TD>
                            </S.TR>
                            <S.TR>
                                <S.TH>DOB</S.TH>
                                <S.TD>{formatDate('MonthDayYear')(dob)}</S.TD>
                            </S.TR>
                            <S.TR>
                                <S.TH>Phone</S.TH>
                                <S.TD>{phone}</S.TD>
                            </S.TR>
                            <S.TR>
                                <S.TH>Email</S.TH>
                                <S.TD>{email}</S.TD>
                            </S.TR>
                            {addressRows}
                            {creditCardRows}
                            {paymentPlanRows}
                            <S.TR>
                                <S.TH>Payment Dashboard</S.TH>
                                <S.TD>
                                    {dashboardUrl ? (
                                        <a href={dashboardUrl}>{dashboardUrl}</a>
                                    ) : (
                                        'N/A - Guarantor has no invoices'
                                    )}
                                </S.TD>
                            </S.TR>
                        </S.TBody>
                    </S.Table>
                );
            };

            return renderDetails();
        },
    ),
);
